import type { MessageKey } from '@dijitalapp/i18n';
import type { SpaceDto, SpaceType } from '@dijitalapp/shared';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams, NavLink } from 'react-router';
import { CommunityPanel } from '../components/CommunityPanel';
import { PageEmpty, PageHeader } from '../components/SectionNav';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useShell } from './Shell';
import { AssistantManager } from '../components/AssistantManager';
import { ASSISTANT_SPACE_TYPES } from '@dijitalapp/shared';
import { Icon } from '../components/Icon';

/** `/spaces/:id`: one space (the list lives in the middle pane); `/spaces/new`: create one. */
export function SpacesPage() {
  const { id, section } = useParams();
  if (id === 'new') return <NewSpace />;
  return section === 'assistant' ? <SpaceAssistant id={id} /> : <SpaceView id={id} />;
}

/** `/spaces/:id/assistant`: the organisation's DijitalApp Assistant (owners and admins). */
function SpaceAssistant({ id }: { id: string | undefined }) {
  const { spaces } = useShell();
  const space = spaces.find((s) => s.id === id);
  if (!space || !id) return null;
  return <AssistantManager spaceId={id} spaceName={space.name} />;
}

function SpaceView({ id }: { id: string | undefined }) {
  const { i18n } = useApp();
  const { t, locale } = i18n;
  const { spaces, activeSpace, experience, selectSpace, reload, setMobileView } = useShell();
  const [error, setError] = useState<string | null>(null);
  const space = (id ? spaces.find((s) => s.id === id) : null) ?? activeSpace;

  // Opening a space makes it the active one (its persona drives the navigation).
  useEffect(() => {
    if (space && space.id !== activeSpace?.id) selectSpace(space.id);
  }, [space, activeSpace, selectSpace]);

  if (!space) return <PageEmpty icon="spaces" title={t('spaces.title')} body={t('app.loading')} />;

  const choosePersona = async (personaKey: string) => {
    try {
      await api('PUT', `/spaces/${space.id}/persona`, { personaKey });
      await reload();
    } catch (err) {
      setError(t(errorMessageKey(err)));
    }
  };

  return (
    <div className="content-inner">
      <PageHeader
        title={space.name}
        description={`${t(`spaces.type.${space.type}` as MessageKey)} · ${t('spaces.members', { count: space.memberCount })} · ${t(`spaces.role.${space.role}` as MessageKey)}`}
        back={() => setMobileView('list')}
      />
      {error && (
        <div className="alert alert-error" role="alert">
          {error}
        </div>
      )}

      {(space.type === 'community' || space.type === 'family') && <CommunityPanel key={space.id} space={space} onLeft={reload} />}

      {(ASSISTANT_SPACE_TYPES as readonly string[]).includes(space.type) && (space.role === 'owner' || space.role === 'admin') && (
        <section className="card assistant-entry" aria-labelledby="assistant-entry">
          <span className="link-mark link-mark-sm" aria-hidden>
            <Icon name="ai" size={24} />
          </span>
          <div>
            <h2 id="assistant-entry" className="card-title">
              {t('assistant.title')} <span className="badge">Business</span>
            </h2>
            <p className="muted">{t('assistant.entryBody')}</p>
          </div>
          <NavLink className="btn btn-primary" to={`/spaces/${space.id}/assistant`}>
            {t('assistant.manage')}
          </NavLink>
        </section>
      )}

      {experience && space.id === activeSpace?.id && (
        <section className="card stack" aria-labelledby="persona-title">
          <div>
            <h2 id="persona-title" className="card-title">
              {t('spaces.persona')}
            </h2>
            <p className="hint">{t('onboarding.personaNote')}</p>
          </div>
          <div className="persona-grid">
            {experience.availablePersonas.map((p) => (
              <button
                key={p.key}
                type="button"
                className="persona-option"
                aria-pressed={experience.persona.key === p.key}
                onClick={() => void choosePersona(p.key)}
              >
                <strong>{p.label[locale] ?? p.label.tr}</strong>
                <span className="muted">{p.description[locale] ?? p.description.tr}</span>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function NewSpace() {
  const { i18n } = useApp();
  const { t } = i18n;
  const { capabilities, selectSpace, reload, setMobileView } = useShell();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [type, setType] = useState<SpaceType>('family');
  const [error, setError] = useState<string | null>(null);
  const creatable = capabilities?.createSpaceTypes ?? [];

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const space = await api<SpaceDto>('POST', '/spaces', { type, name });
      await reload();
      selectSpace(space.id);
      navigate(`/spaces/${space.id}`, { replace: true });
    } catch (err) {
      setError(t(errorMessageKey(err)));
    }
  };

  return (
    <div className="content-inner">
      <PageHeader title={t('spaces.create')} description={t('spaces.createDesc')} back={() => setMobileView('list')} />
      {creatable.length === 0 ? (
        <PageEmpty icon="spaces" title={t('spaces.create')} body={t('spaces.cannotCreate')} />
      ) : (
        <form className="card stack" onSubmit={create}>
          <div className="field">
            <label htmlFor="space-name">{t('spaces.name')}</label>
            <input id="space-name" className="input" required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <span className="field-legend">{t('spaces.type')}</span>
            <div className="choice-grid" role="radiogroup" aria-label={t('spaces.type')}>
              {creatable.map((st) => (
                <button key={st} type="button" role="radio" aria-checked={type === st} className="choice" onClick={() => setType(st)}>
                  <strong>{t(`spaces.type.${st}` as MessageKey)}</strong>
                  <span className="muted">{t(`spaces.typeDesc.${st}` as MessageKey)}</span>
                </button>
              ))}
            </div>
          </div>
          {(capabilities?.verificationRequiredSpaceTypes.length ?? 0) > 0 && <p className="hint">{t('spaces.verificationRequired')}</p>}
          {error && (
            <div className="alert alert-error" role="alert">
              {error}
            </div>
          )}
          <div className="card-actions">
            <button className="btn btn-primary">{t('spaces.create')}</button>
          </div>
        </form>
      )}
    </div>
  );
}
